import { create } from 'zustand'
import {
  Client,
  CreateClientData,
  UpdateClientData,
  CreateClientResult,
  ClientDuplicateWarning,
  listClients,
  createClient as apiCreate,
  updateClient as apiUpdate,
  deleteClient as apiDelete,
  setClientExternalId,
  removeClientExternalId,
  getErrorMessage,
} from '../api/clientsApi'

interface ClientsState {
  clients: Client[]
  isLoading: boolean
  error: string | null
  duplicateWarning: ClientDuplicateWarning | null
  lastFetchParams: { type?: string; isActive?: string }

  fetchClients: (params?: { type?: string; isActive?: string }) => Promise<void>
  createClient: (data: CreateClientData) => Promise<CreateClientResult | null>
  updateClient: (id: string, data: UpdateClientData) => Promise<boolean>
  deleteClient: (id: string) => Promise<boolean>
  clearError: () => void
  clearDuplicateWarning: () => void
}

export const useClientsStore = create<ClientsState>()((set, get) => ({
  clients: [],
  isLoading: false,
  error: null,
  duplicateWarning: null,
  lastFetchParams: {},

  fetchClients: async (params = {}) => {
    set({ isLoading: true, error: null, lastFetchParams: params })

    try {
      const response = await listClients({ ...params, limit: 100 })
      set({ clients: response.clients, isLoading: false })
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
    }
  },

  createClient: async (data: CreateClientData) => {
    set({ isLoading: true, error: null, duplicateWarning: null })

    try {
      const result = await apiCreate(data)
      if (result.created) {
        // Refetch to maintain sort order
        const response = await listClients({ ...get().lastFetchParams, limit: 100 })
        set({ clients: response.clients, isLoading: false })
      } else {
        set({ duplicateWarning: result.duplicateWarning, isLoading: false })
      }
      return result
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
      return null
    }
  },

  updateClient: async (id: string, data: UpdateClientData & { externalIds?: Record<string, string> }) => {
    set({ isLoading: true, error: null })

    try {
      const { externalIds, ...patchData } = data

      // Diff externalIds and make targeted calls
      if (externalIds !== undefined) {
        const current = get().clients.find((c) => c._id === id)
        const currentIds = current?.externalIds ?? {}

        // Set new or changed keys
        for (const [system, value] of Object.entries(externalIds)) {
          if (currentIds[system] !== value) {
            await setClientExternalId(id, system, value)
          }
        }

        // Remove keys that were deleted in the form
        for (const system of Object.keys(currentIds)) {
          if (!(system in externalIds)) {
            await removeClientExternalId(id, system)
          }
        }
      }

      await apiUpdate(id, patchData)
      const response = await listClients({ ...get().lastFetchParams, limit: 100 })
      set({ clients: response.clients, isLoading: false })
      return true
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
      return false
    }
  },

  deleteClient: async (id: string) => {
    set({ isLoading: true, error: null })

    try {
      await apiDelete(id)
      set((state) => ({
        clients: state.clients.filter((c) => c._id !== id),
        isLoading: false,
      }))
      return true
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
      return false
    }
  },

  clearError: () => set({ error: null }),
  clearDuplicateWarning: () => set({ duplicateWarning: null }),
}))
